import { CommonModule } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnInit,
  output,
  signal
} from '@angular/core'
import { VisioComponentsModule } from '@visio-ai/ui-kit'
import { AllUserEstabs, UserListItem } from '../../../../_services/users/users.model'
import { FilterOption, ListFilterSelectedPayload } from '../../models/list-filter.model'
import { ScheduleRule } from '../../models/schedule-rule.model'
import { AuthService } from '../../services/auth.service'

export type ScheduleRulesFilters = {
  active: FilterOption[]
  shopIds: FilterOption[]
  userIds: FilterOption[]
  products: FilterOption[]
}

@Component({
  selector: 'app-schedule-rules-filters',
  standalone: true,
  imports: [CommonModule, VisioComponentsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './schedule-rules-filters.component.html',
  styleUrl: './schedule-rules-filters.component.scss'
})
export class ScheduleRulesFiltersComponent implements OnInit {
  private authService = inject(AuthService)
  loggedUserId = signal('')

  ngOnInit(): void {
    const user = this.authService.getLoggedUser()
    // console.log(user)
    if (user) {
      this.loggedUserId.set(user._id)
    }
  }

  filterState = signal<ScheduleRulesFilters>({
    active: [],
    shopIds: [],
    products: [],
    userIds: []
  })

  rulesList = input.required<ScheduleRule[]>()
  filteredRules = output<ScheduleRule[]>()

  shopsList = input.required<AllUserEstabs[]>()
  usersList = input.required<UserListItem[]>()

  activeOptions: FilterOption[] = [
    { text: 'Ativo', value: 'true' },
    { text: 'Inativo', value: 'false' }
  ]

  shopIdsOptions = computed(() => {
    return this.shopsList().map(shop => ({ text: shop.nome, value: shop._id }))
  })

  userIdsOptions = computed(() => {
    return [
      ...(this.loggedUserId()
        ? [{ text: `Eu (${this.loggedUserId()})`, value: this.loggedUserId() }]
        : []),
      ...this.usersList().map(user => ({ text: user.nome, value: user._id }))
    ]
  })

  productsOptions: FilterOption[] = [
    { text: 'Checkout', value: 'checkout' },
    { text: 'Pista', value: 'pista' }
  ]

  filteredList = computed(() => {
    const filterState = this.filterState()
    const filtered = this.rulesList().filter(rule => {
      if (
        filterState.active.length > 0 &&
        !filterState.active.some(option => option.value === `${rule.active}`)
      ) {
        return false
      }
      if (
        filterState.shopIds.length > 0 &&
        !filterState.shopIds.some(option => option.value === rule.shop_id)
      ) {
        return false
      }
      if (
        filterState.userIds.length > 0 &&
        !filterState.userIds.some(option => option.value === rule.user_in_charge)
      ) {
        return false
      }
      if (
        filterState.products.length > 0 &&
        !filterState.products.some(option => option.value === rule.product)
      ) {
        return false
      }
      return true
    })

    filtered.sort((a, b) => {
      const shopA = this.shopsList().find(shop => shop._id === a.shop_id)
      const shopB = this.shopsList().find(shop => shop._id === b.shop_id)
      return (shopA?.nome || '').localeCompare(shopB?.nome || '')
    })

    this.filteredRules.emit(filtered)
    return filtered
  })

  totalCount = computed(() => this.rulesList().length)
  filteredCount = computed(() => this.filteredList().length)

  hasActiveFilters = computed(() => {
    return (
      this.filterState().active.length > 0 ||
      this.filterState().shopIds.length > 0 ||
      this.filterState().userIds.length > 0 ||
      this.filterState().products.length > 0
    )
  })

  toggleActiveFilterStatus(payload: ListFilterSelectedPayload) {
    this.filterState.update(state => ({
      ...state,
      active: payload
    }))
  }

  toggleShopIdsFilterStatus(payload: ListFilterSelectedPayload) {
    this.filterState.update(state => ({
      ...state,
      shopIds: payload
    }))
  }

  toggleUserIdsFilterStatus(payload: ListFilterSelectedPayload) {
    this.filterState.update(state => ({
      ...state,
      userIds: payload
    }))
  }

  toggleProductsFilterStatus(payload: ListFilterSelectedPayload) {
    this.filterState.update(state => ({
      ...state,
      products: payload
    }))
  }
}
